import type {
  Command,
  Dependencias,
} from '@/features/proposta/entities/Comprador/form-to-listagem/comprador-listagem-command-factory'
import {
  tranformarCompradorFormParaCompradorProposta,
  gerarListaDeClientes,
  gerarClienteConjuge,
} from '@/features/proposta/entities/Comprador/form-to-listagem/mapper-comprador-pf-para-listagem'
import type {
  TClientePessoaFisicaPropostaSchema,
  TCompradorPropostaSchema,
  TProcuradorPropostaSchema,
} from '@/features/proposta/schemas'
import type { CompradorPessoaFisicaStepSchemaInput } from '@/features/proposta/schemas/compradores/comprador-pf.schema'

export class AdicionarPessoaFisicaCommand implements Command {
  constructor(
    private readonly comprador: CompradorPessoaFisicaStepSchemaInput,
    private readonly clientes: () => TClientePessoaFisicaPropostaSchema[],
    private readonly compradores: () => TCompradorPropostaSchema[],
    private readonly deps: Dependencias,
  ) {}

  public atualizaCompradorComAssinaturaDupla(
    index: number,
    compradores: TCompradorPropostaSchema[],
    comprador: TCompradorPropostaSchema,
  ) {
    if (!compradores[index]) return
    this.deps.atualizarComprador(index, {
      ...compradores[index],
      tipoAssinatura: comprador.tipoAssinatura,
      vinculos: [
        ...compradores[index].vinculos,
        ...comprador.vinculos.filter(
          (novoVinculo) =>
            !compradores[index]!.vinculos.some(
              (vinculoExistente) =>
                vinculoExistente.clienteId === novoVinculo.clienteId && vinculoExistente.tipo === novoVinculo.tipo,
            ),
        ),
      ],
    })
  }

  executar() {
    const lista = gerarListaDeClientes(this.comprador)
    const compradores = this.compradores()
    const comprador = tranformarCompradorFormParaCompradorProposta(this.comprador)
    for (const novoPF of lista) {
      const index = this.clientes().findIndex((cliente) => cliente.id === novoPF.id)
      if (index === -1) {
        this.deps.adicionarCliente(novoPF)
      } else {
        this.deps.atualizarCliente(index, novoPF)
      }
    }
    const indexComprador = compradores.findIndex((c) => c.clienteId === comprador.clienteId)
    const compradorNaoCadastrado = indexComprador === -1
    if (compradorNaoCadastrado) return this.deps.adicionarComprador(comprador)
    this.atualizaCompradorComAssinaturaDupla(indexComprador, compradores, comprador)
  }
}

export class AtualizarPessoaFisicaCommand implements Command {
  constructor(
    private readonly comprador: CompradorPessoaFisicaStepSchemaInput,
    private readonly clientes: () => TClientePessoaFisicaPropostaSchema[],
    private readonly compradores: () => TCompradorPropostaSchema[],
    private readonly procuradores: () => TProcuradorPropostaSchema[],
    private readonly deps: Dependencias,
    private readonly clienteIdToUpdate: string | null,
  ) {}

  atualizarMenorIdade(clientes: TClientePessoaFisicaPropostaSchema[]) {
    const cliente = clientes.find((c) => this.clienteIdToUpdate === c.id)
    if (!cliente) return
    const novoCliente: TClientePessoaFisicaPropostaSchema = {
      ...cliente,
      nome: this.comprador.dados.nome,
      nacionalidadeId: this.comprador.dados.nacionalidadeId,
      profissaoId: this.comprador.dados.profissaoId,
      email: this.comprador.dados.email ?? cliente.email,
    }
    const index = this.clientes().findIndex((c) => c.id === novoCliente.id)
    this.deps.atualizarCliente(index, novoCliente)
  }

  atualizarCompradorEConjuge(indice: number) {
    const clientes = this.clientes()
    const compradores = this.compradores()
    const procuradores = this.procuradores()
    const vinculosComprador = compradores.flatMap((c) => c.vinculos)
    const clienteIndex = clientes.findIndex((c) => c.id === this.clienteIdToUpdate)

    const vinculosAtualizados = compradores[indice]!.vinculos.filter((vinculo) => {
      const deveLimparConjuge =
        vinculo.tipo === 'Conjuge' &&
        (this.comprador.estadoCivil?.tipoAssinatura == 'Simples' ||
          !(
            this.comprador.estadoCivil?.regimeCasamento?.includes('Comunhao') ||
            this.comprador.estadoCivil?.regimeCasamento?.includes('ParticipacaoFinalNosAquestos')
          ))

      const clienteVinculadoApenasAUmComprador =
        vinculosComprador.filter((v) => v.clienteId === vinculo.clienteId && v.tipo === 'Conjuge').length === 1

      const vinculoEhProcurador = procuradores.find((procurador) => procurador.procuradorId === vinculo.clienteId)

      if (deveLimparConjuge && clienteVinculadoApenasAUmComprador && !vinculoEhProcurador) {
        this.deps.removeCliente(clientes.findIndex((cliente) => cliente.id === vinculo.clienteId))
      }

      return !deveLimparConjuge
    })
    const compradorForm = tranformarCompradorFormParaCompradorProposta(this.comprador)
    this.deps.atualizarCliente(clienteIndex, {
      ...clientes[clienteIndex]!,
      email: this.comprador.contato?.email ?? '',
      nacionalidadeId: this.comprador.dados.nacionalidadeId,
      profissaoId: this.comprador.dados.profissaoId,
      nome: this.comprador.dados.nome,
      telefone: {
        numero: this.comprador.contato?.telefone?.numero.replaceAll(/\D/g, '') ?? '',
        origem: this.comprador.contato?.telefone?.origem ?? 'Brasil',
        principal: true,
      },
      possuiUniaoEstavel: !!this.comprador.estadoCivil?.uniaoEstavel,
      estadoCivil: this.comprador.estadoCivil!.status,
      regimeCasamento: this.comprador.estadoCivil?.regimeCasamento ?? null,
    })

    if (compradorForm.tipoAssinatura === 'Dupla') {
      const conjugeIndex = clientes.findIndex((c) => c.id === this.comprador.conjuge?.dados.id)
      this.deps.atualizarCliente(conjugeIndex, {
        ...clientes[conjugeIndex]!,
        regimeCasamento: this.comprador.estadoCivil?.regimeCasamento,
        possuiUniaoEstavel: this.comprador.estadoCivil?.uniaoEstavel === 'sim',
        estadoCivil: this.comprador.estadoCivil!.status,
      })
    }

    return this.deps.atualizarComprador(indice, {
      clienteId: compradorForm.clienteId,
      tipo: compradorForm.tipo,
      tipoAssinatura: compradorForm.tipoAssinatura,
      vinculos: vinculosAtualizados,
    })
  }

  atualizarConjuge(clientes: TClientePessoaFisicaPropostaSchema[]) {
    const conjuge = gerarClienteConjuge(this.comprador.conjuge, this.comprador.estadoCivil)
    const clienteIndex = clientes.findIndex((c) => c.id === conjuge?.id)
    if (!clientes[clienteIndex] || !conjuge) return

    this.deps.atualizarCliente(clienteIndex, {
      ...clientes[clienteIndex],
      nome: conjuge.nome,
      nacionalidadeId: conjuge.nacionalidadeId,
      profissaoId: conjuge.profissaoId,
      email: conjuge.email,
      possuiUniaoEstavel: false,
      estadoCivil: conjuge.estadoCivil,
      regimeCasamento: conjuge.regimeCasamento,
      telefone: {
        numero: conjuge.telefone.numero.replaceAll(/\D/g, ''),
        origem: conjuge.telefone.origem ?? 'Brasil',
        principal: true,
      },
    })
  }

  executar() {
    const clientes = this.clientes()
    const compradores = this.compradores()
    const clienteCompradorIndex = clientes.findIndex((c) => c.id === this.clienteIdToUpdate)
    if (clienteCompradorIndex === -1) throw new Error(`Cliente com ID ${this.comprador.dados.id} não encontrado.`)

    if (this.comprador.fluxoIndependente === 'Conjuge') return this.atualizarConjuge(clientes)
    if (!clientes[clienteCompradorIndex]?.maiorDe18Anos) return this.atualizarMenorIdade(clientes)

    const indiceComprador = compradores.findIndex((c) => c.clienteId === this.clienteIdToUpdate)
    const compradorPossuiConjuge = compradores[indiceComprador]?.vinculos.some((vinculo) => vinculo.tipo === 'Conjuge')
    if (compradorPossuiConjuge) return this.atualizarCompradorEConjuge(indiceComprador)

    this.deps.atualizarComprador(indiceComprador, {
      ...compradores[indiceComprador]!,
      tipoAssinatura: this.comprador.estadoCivil?.tipoAssinatura ?? null,
    })

    return this.deps.atualizarCliente(clienteCompradorIndex, {
      ...clientes[clienteCompradorIndex],
      email: this.comprador.contato?.email ?? '',
      nacionalidadeId: this.comprador.dados.nacionalidadeId,
      profissaoId: this.comprador.dados.profissaoId,
      nome: this.comprador.dados.nome,
      telefone: {
        numero: this.comprador.contato?.telefone?.numero.replaceAll(/\D/g, '') ?? '',
        origem: this.comprador.contato?.telefone?.origem ?? 'Brasil',
        principal: true,
      },
      possuiUniaoEstavel: this.comprador.estadoCivil?.uniaoEstavel
        ? this.comprador.estadoCivil?.uniaoEstavel === 'sim'
        : null,
      estadoCivil: this.comprador.estadoCivil!.status,
      regimeCasamento: this.comprador.estadoCivil?.regimeCasamento ?? null,
    })
  }
}
